import React, { useEffect, useRef } from 'react';
import { StyleSheet, View, Text, TouchableOpacity, Animated } from 'react-native';
import { useStrokeStore } from '../store/useStrokeStore';
import { useNotificationStore } from '../store/NotificationStore';
import { useActivityStore } from '../store/useActivityStore';

export const TeacherFeedbackOverlay: React.FC = () => {
    const { teacherSuggestion, setTeacherSuggestion, activeToast, setToast, clearStrokes } = useStrokeStore();
    const { markAsRead } = useNotificationStore();
    const { jumpToActivity } = useActivityStore();

    // Animation for Active Suggestion (Sticky)
    const suggestionAnim = useRef(new Animated.Value(-150)).current;

    // Animation for Toast (Alert)
    const toastAnim = useRef(new Animated.Value(-150)).current;

    // --- Active Suggestion Logic ---
    useEffect(() => {
        if (teacherSuggestion) {
            Animated.spring(suggestionAnim, {
                toValue: 20,
                useNativeDriver: true,
                friction: 6,
                tension: 40
            }).start();
        } else {
            Animated.timing(suggestionAnim, {
                toValue: -150,
                duration: 200,
                useNativeDriver: true,
            }).start();
        }
    }, [teacherSuggestion]);

    // --- Toast Logic ---
    useEffect(() => {
        if (activeToast) {
            Animated.spring(toastAnim, {
                toValue: 20,
                useNativeDriver: true,
                friction: 6,
                tension: 40
            }).start();

            // Auto-hide toast after 6 seconds
            const timer = setTimeout(() => {
                dismissToast();
            }, 6000);
            return () => clearTimeout(timer);
        } else {
            Animated.timing(toastAnim, {
                toValue: -150,
                duration: 200,
                useNativeDriver: true,
            }).start();
        }
    }, [activeToast]);

    const dismissToast = () => {
        setToast(null);
    };

    const handleToastClick = () => {
        if (!activeToast) return;

        const notification = activeToast;
        markAsRead(activeToast.id);

        // Switch activity if needed
        if (notification.targetText) {
            const { activities, currentActivityIndex } = useActivityStore.getState();
            const currentActivity = activities[currentActivityIndex];
            if ((currentActivity?.config as any)?.targetText !== notification.targetText) {
                clearStrokes();
                jumpToActivity(notification.targetText);
            }
        }

        // Activate Feedback View
        setTeacherSuggestion({
            feedback: notification.feedback,
            overlay: notification.overlay,
            targetText: notification.targetText
        });

        setToast(null);
    };

    return (
        <>
            {/* PINNED ACTIVE FEEDBACK (Priority) */}
            {teacherSuggestion && (
                <Animated.View style={[styles.container, styles.activeContainer, { transform: [{ translateY: suggestionAnim }] }]}>
                    <View style={styles.content} accessibilityRole="summary" accessibilityLabel="Teacher review mode">
                        <View style={[styles.iconContainer, styles.activeIconBg]}>
                            <Text style={styles.icon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>👩‍🏫</Text>
                        </View>
                        <View style={styles.textContainer}>
                            <Text style={[styles.title, styles.activeTitle]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Review Mode</Text>
                            <Text style={styles.message} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{String(teacherSuggestion.feedback ?? '')}</Text>
                        </View>
                        <TouchableOpacity onPress={() => setTeacherSuggestion(null)} style={styles.actionButton} accessibilityRole="button" accessibilityLabel="Done" accessibilityHint="Double tap to dismiss the teacher feedback">
                            <Text style={styles.actionButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Done</Text>
                        </TouchableOpacity>
                    </View>
                    <View style={styles.activeBorder} />
                </Animated.View>
            )}

            {/* INCOMING TOAST (Lower priority, clickable) */}
            {activeToast && !teacherSuggestion && (
                <Animated.View style={[styles.container, { transform: [{ translateY: toastAnim }] }]}>
                    <TouchableOpacity activeOpacity={0.9} onPress={handleToastClick} style={styles.content} accessibilityRole="button" accessibilityLabel="New teacher feedback" accessibilityHint="Double tap to view the feedback">
                        <View style={styles.iconContainer}>
                            <Text style={styles.icon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🔔</Text>
                        </View>
                        <View style={styles.textContainer}>
                            <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>New Feedback</Text>
                            <Text style={styles.message} numberOfLines={1} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                For {activeToast.targetText ? `Activity ${String(activeToast.targetText)}` : 'Generic'}: {String(activeToast.feedback ?? '')}
                            </Text>
                        </View>
                        <View style={styles.toastActions}>
                            <Text style={styles.viewText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>View</Text>
                            <TouchableOpacity onPress={(e) => { e.stopPropagation(); dismissToast(); }} style={styles.closeButton} accessibilityRole="button" accessibilityLabel="Dismiss feedback" accessibilityHint="Double tap to close this notification">
                                <Text style={styles.closeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✕</Text>
                            </TouchableOpacity>
                        </View>
                    </TouchableOpacity>
                </Animated.View>
            )}
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        top: 20,
        left: 20,
        right: 20,
        zIndex: 1000,
        // Center horizontally for wider screens if needed
        maxWidth: 600,
        alignSelf: 'center',
    },
    activeContainer: {
        zIndex: 1001, // Pinned takes precedence
    },
    content: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 16,
        flexDirection: 'row',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 8,
    },
    activeBorder: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: 6,
        backgroundColor: '#EF4444', // Red for active correction
        borderTopLeftRadius: 16,
        borderBottomLeftRadius: 16,
    },
    iconContainer: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: '#F0F9FF',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    activeIconBg: {
        backgroundColor: '#FEF2F2',
    },
    icon: {
        fontSize: 24,
    },
    textContainer: {
        flex: 1,
    },
    title: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#3B82F6',
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 2,
    },
    activeTitle: {
        color: '#EF4444',
    },
    message: {
        fontSize: 15,
        color: '#1E293B',
        fontWeight: '600',
    },
    closeButton: {
        padding: 12,
        minHeight: 56,
        minWidth: 56,
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 4,
    },
    closeText: {
        fontSize: 16,
        color: '#94A3B8',
        fontWeight: 'bold',
    },
    actionButton: {
        backgroundColor: '#EF4444',
        paddingHorizontal: 16,
        paddingVertical: 12,
        minHeight: 56,
        minWidth: 56,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 20,
        marginLeft: 8,
    },
    actionButtonText: {
        color: '#FFF',
        fontWeight: 'bold',
        fontSize: 13,
    },
    toastActions: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 8,
    },
    viewText: {
        color: '#3B82F6',
        fontWeight: 'bold',
        fontSize: 14,
        marginRight: 8,
    }
});
